// AGC: Mirror Firebase Analytics events into Firestore for in-app admin dashboard.
// AGC: Fire-and-forget from the caller's perspective. Batches writes (5s debounce OR 10 events)
// AGC: to reduce Firestore cost. Never throws. Never delays the analytics wrapper.
import { writeBatch, collection, doc, serverTimestamp } from 'firebase/firestore'
import { db } from '../lib/firebase'

export const BATCH_SIZE = 10
export const MAX_BUFFER = 1000
const FLUSH_INTERVAL_MS = 5000
const COLLECTION = 'analytics_events'

interface BufferedEvent {
  name: string
  params: Record<string, unknown>
  userId: string
}

let buffer: BufferedEvent[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null

// AGC: Mirrors analytics.ts dev-suppression guard — skip in dev unless VITE_ANALYTICS_DEV is set
function shouldSuppressInDev(): boolean {
  return import.meta.env.DEV && !import.meta.env.VITE_ANALYTICS_DEV
}

async function flushBuffer(): Promise<void> {
  if (flushTimer) {
    clearTimeout(flushTimer)
    flushTimer = null
  }
  if (buffer.length === 0) return
  const toFlush = buffer.splice(0, BATCH_SIZE)
  try {
    const batch = writeBatch(db)
    const col = collection(db, COLLECTION)
    for (const evt of toFlush) {
      const ref = doc(col)
      batch.set(ref, {
        name: evt.name,
        params: evt.params,
        userId: evt.userId,
        timestamp: serverTimestamp(),
      })
    }
    await batch.commit()
  } catch (err) {
    console.warn('[analytics-mirror] flush failed', err)
    // AGC: events are dropped on failure — do not retry infinitely
  }
  // AGC: if more events buffered during flush, schedule another round
  if (buffer.length > 0) {
    scheduleFlush()
  }
}

function scheduleFlush(): void {
  if (flushTimer) return
  flushTimer = setTimeout(() => {
    void flushBuffer()
  }, FLUSH_INTERVAL_MS)
}

// AGC: Public entry point — buffers one event and triggers flush when ready.
// AGC: Returns void intentionally — caller must not await or handle errors.
export function mirrorEventToFirestore(
  name: string,
  params: Record<string, unknown>,
  userId: string | null
): void {
  // AGC: Skip in dev unless analytics dev flag is set
  if (shouldSuppressInDev()) return
  // AGC: Skip unauthenticated writes — Firestore rules require userId == request.auth.uid
  if (!userId) return
  buffer.push({ name, params, userId })
  // AGC: Cap the buffer to avoid unbounded memory growth when Firestore is down
  while (buffer.length > MAX_BUFFER) {
    buffer.shift()
  }
  if (buffer.length >= BATCH_SIZE) {
    void flushBuffer()
    return
  }
  scheduleFlush()
}

// AGC: Test-only helper — flush immediately and wait for the promise to resolve
export async function _flushNow(): Promise<void> {
  await flushBuffer()
}

// AGC: Test-only helper — reset all module state between tests
export function _resetForTests(): void {
  buffer = []
  if (flushTimer) {
    clearTimeout(flushTimer)
    flushTimer = null
  }
}
